import { footprintExtent, footprintHeight, footprintSurface, getFootprint, type Footprint } from "./footprints.js";
import { plotRadiusForTier } from "./plots.js";
import { PLACEMENT_SCALE_STEPS, placementScale, type HomeType, type ItemPlacement, type RoomId } from "./types.js";

/**
 * Where a thing is allowed to stand.
 *
 * One module, called by both the activity and the worker, so an optimistic placement can never
 * disagree with what the server will accept. Mirrors `evaluateHomeChoice` in homes.ts.
 *
 * Two shapes bound a yard. The parcel is a circle, which is also exactly the ground `terrain.ts`
 * levels flat, so staying inside it is the same guarantee as standing on level ground. The cottage
 * is a box in the middle of it, and items keep out of that box entirely.
 *
 * A room is the simpler case: one rectangle, no keep-out, and a ceiling. It shares `placementBounds`
 * with the yard, because a quarter turn permutes an item's corners the same way wherever it stands.
 */

/** Breathing room at the fence, so nothing looks like it is balanced on the boundary. */
export const PLACEMENT_EDGE_MARGIN = 0.5;

/**
 * How much ground each home type occupies, as half-extents from the plot centre.
 *
 * The whole silhouette, roof included — an item tucked under an eave reads as clipping into the
 * house even when its footprint technically clears the wall.
 */
export const HOUSE_KEEPOUT: Record<HomeType, { halfX: number; halfZ: number }> = {
  hobbit_hole: { halfX: 6, halfZ: 6 },
  tudor_cottage: { halfX: 6, halfZ: 6 },
  cozy_cabin: { halfX: 6, halfZ: 6 }
};

export type PlacementBounds = {
  /** Centre of the footprint relative to the placement's own origin, after rotation and scale. */
  offsetX: number;
  offsetZ: number;
  halfX: number;
  halfZ: number;
};

/**
 * The footprint as it sits after a quarter turn and a size step.
 *
 * Quarter turns permute the corners, so this is an axis swap and some sign flips rather than any
 * trigonometry — which is what keeps the containment test exact instead of merely close.
 */
export function placementBounds(footprint: Footprint, rotationQuarters: number, scaleStep: number): PlacementBounds {
  const { offsetX, offsetZ, halfX, halfZ } = footprintExtent(footprint);
  const scale = placementScale(scaleStep);
  const quarters = ((rotationQuarters % 4) + 4) % 4;

  // Rotating clockwise about Y sends +X to -Z and +Z to +X.
  const rotated =
    quarters === 0 ? { x: offsetX, z: offsetZ, hx: halfX, hz: halfZ }
    : quarters === 1 ? { x: -offsetZ, z: offsetX, hx: halfZ, hz: halfX }
    : quarters === 2 ? { x: -offsetX, z: -offsetZ, hx: halfX, hz: halfZ }
    : { x: offsetZ, z: -offsetX, hx: halfZ, hz: halfX };

  return {
    offsetX: rotated.x * scale,
    offsetZ: rotated.z * scale,
    halfX: rotated.hx * scale,
    halfZ: rotated.hz * scale
  };
}

/** True when every corner of the footprint sits inside the parcel. */
export function isWithinPlot(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  plotTier: number;
}): boolean {
  const limit = plotRadiusForTier(input.plotTier) - PLACEMENT_EDGE_MARGIN;
  if (limit <= 0) return false;
  const centreX = input.position.x + input.bounds.offsetX;
  const centreZ = input.position.z + input.bounds.offsetZ;
  // The farthest corner is always the one pushed away from the origin on both axes.
  const reach = Math.hypot(Math.abs(centreX) + input.bounds.halfX, Math.abs(centreZ) + input.bounds.halfZ);
  return reach <= limit;
}

/** True when the footprint overlaps the cottage. An empty parcel has nothing to keep out of. */
export function overlapsHouse(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  homeType: HomeType | null;
}): boolean {
  if (!input.homeType) return false;
  const keepOut = HOUSE_KEEPOUT[input.homeType];
  const centreX = input.position.x + input.bounds.offsetX;
  const centreZ = input.position.z + input.bounds.offsetZ;
  return (
    Math.abs(centreX) < keepOut.halfX + input.bounds.halfX &&
    Math.abs(centreZ) < keepOut.halfZ + input.bounds.halfZ
  );
}

/**
 * Pulls a position back inside the fence, for live dragging.
 *
 * The legal region is star-shaped about the plot centre, so drawing the centre straight back
 * toward the origin always reaches it. Solving for the exact fraction rather than stepping
 * toward it matters: a clamp that stopped short of what `isWithinPlot` accepts would leave the
 * member unable to reach positions the server would have allowed.
 */
export function clampToPlot(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  plotTier: number;
}): { x: number; z: number } {
  if (isWithinPlot(input)) return { x: input.position.x, z: input.position.z };

  // Solve for the boundary a hair inside the real one. The solution lands exactly on whatever
  // limit is used, and "exactly on" rounds to "just outside" often enough that the clamp would
  // otherwise hand back positions its own containment test rejects.
  const limit = plotRadiusForTier(input.plotTier) - PLACEMENT_EDGE_MARGIN - 1e-9;
  const centreX = input.position.x + input.bounds.offsetX;
  const centreZ = input.position.z + input.bounds.offsetZ;

  // Solve |t * centre| + half == limit along the ray from the origin through the footprint centre:
  //   (cx² + cz²) t² + 2(hx|cx| + hz|cz|) t + (hx² + hz² - limit²) = 0
  const a = centreX * centreX + centreZ * centreZ;
  const b = 2 * (input.bounds.halfX * Math.abs(centreX) + input.bounds.halfZ * Math.abs(centreZ));
  const c = input.bounds.halfX * input.bounds.halfX + input.bounds.halfZ * input.bounds.halfZ - limit * limit;

  // The item is wider than the whole parcel; nothing along this ray is legal.
  if (c >= 0) return { x: -input.bounds.offsetX, z: -input.bounds.offsetZ };
  if (a <= 1e-9) return { x: input.position.x, z: input.position.z };

  const t = Math.min(1, Math.max(0, (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a)));
  return {
    x: centreX * t - input.bounds.offsetX,
    z: centreZ * t - input.bounds.offsetZ
  };
}

export type PlacementCheck =
  | { ok: true }
  | {
      ok: false;
      reason: "outside_plot" | "inside_house" | "too_large" | "outside_room" | "too_tall" | "occupied";
      message: string;
    };

/**
 * Whether a single exterior placement is legal, and if not, why in terms a member can act on.
 *
 * The `too_large` case is the one worth distinguishing: an item that cannot fit anywhere at its
 * current size is not a mistake the member made by dragging, and telling them to move it would be
 * useless advice. They need to shrink it or expand the parcel.
 */
export function evaluateExteriorPlacement(input: {
  itemId: string;
  itemName?: string;
  position: { x: number; z: number };
  rotationQuarters: number;
  scaleStep: number;
  plotTier: number;
  homeType: HomeType | null;
}): PlacementCheck {
  const footprint = getFootprint(input.itemId);
  const bounds = placementBounds(footprint, input.rotationQuarters, input.scaleStep);
  const label = input.itemName ?? "That item";

  if (!fitsAnywhere({ footprint, scaleStep: input.scaleStep, plotTier: input.plotTier, homeType: input.homeType })) {
    const canShrink = input.scaleStep > 0;
    return {
      ok: false,
      reason: "too_large",
      message: canShrink
        ? `${label} needs more room than your yard has. Make it smaller, or expand your parcel.`
        : `${label} needs more room than your yard has. Expand your parcel to fit it.`
    };
  }
  if (!isWithinPlot({ position: input.position, bounds, plotTier: input.plotTier })) {
    return { ok: false, reason: "outside_plot", message: `${label} does not fit inside your parcel there` };
  }
  if (overlapsHouse({ position: input.position, bounds, homeType: input.homeType })) {
    return { ok: false, reason: "inside_house", message: `${label} would sit inside your home` };
  }
  return { ok: true };
}

/**
 * The tightest spots an item can stand in: straight out from each face of the cottage.
 *
 * On a circle with a box cut out of the middle, the roomiest position is always square to a face.
 * A diagonal gives up corner clearance against the fence without buying anything back against the
 * keep-out. Both the "does this fit at all" test and the "where shall we drop it" search read from
 * this same list, so they can never disagree about what is possible.
 */
function faceCandidates(
  bounds: PlacementBounds,
  keepOut: { halfX: number; halfZ: number },
  hasHouse: boolean,
  push = 0
): { x: number; z: number }[] {
  const gap = hasHouse ? 1e-6 : 0;
  const outX = keepOut.halfX + bounds.halfX + gap + push;
  const outZ = keepOut.halfZ + bounds.halfZ + gap + push;
  return [
    { x: outX - bounds.offsetX, z: -bounds.offsetZ },
    { x: -outX - bounds.offsetX, z: -bounds.offsetZ },
    { x: -bounds.offsetX, z: outZ - bounds.offsetZ },
    { x: -bounds.offsetX, z: -outZ - bounds.offsetZ }
  ];
}

/** Whether an item has any legal position at all at a given size. */
export function fitsAnywhere(input: {
  footprint: Footprint;
  scaleStep: number;
  plotTier: number;
  homeType: HomeType | null;
}): boolean {
  const keepOut = input.homeType ? HOUSE_KEEPOUT[input.homeType] : { halfX: 0, halfZ: 0 };

  for (let quarters = 0; quarters < 4; quarters += 1) {
    const bounds = placementBounds(input.footprint, quarters, input.scaleStep);
    for (const position of faceCandidates(bounds, keepOut, Boolean(input.homeType))) {
      if (
        isWithinPlot({ position, bounds, plotTier: input.plotTier }) &&
        !overlapsHouse({ position, bounds, homeType: input.homeType })
      ) {
        return true;
      }
    }
  }
  return false;
}

/** The largest size an item can be placed at on this parcel, or null if even the smallest fails. */
export function largestFittingScaleStep(input: {
  itemId: string;
  plotTier: number;
  homeType: HomeType | null;
}): number | null {
  const footprint = getFootprint(input.itemId);
  for (let step = PLACEMENT_SCALE_STEPS.length - 1; step >= 0; step -= 1) {
    if (fitsAnywhere({ footprint, scaleStep: step, plotTier: input.plotTier, homeType: input.homeType })) {
      return step;
    }
  }
  return null;
}

/**
 * A spot to drop a newly placed item: clear of the cottage, inside the fence, and preferably not
 * on top of something else.
 *
 * Searches the four faces first, since those are the only places a tightly-fitting item can go,
 * then works outward and around for roomier items so a yard does not end up with everything in a
 * neat cross. Falls back to any legal spot before giving up — being asked to move something is a
 * better outcome than being told there is nowhere at all.
 */
export function defaultPlacementPosition(input: {
  itemId: string;
  rotationQuarters: number;
  scaleStep: number;
  plotTier: number;
  homeType: HomeType | null;
  taken: ItemPlacement[];
}): { x: number; z: number } | null {
  const footprint = getFootprint(input.itemId);
  const bounds = placementBounds(footprint, input.rotationQuarters, input.scaleStep);
  const keepOut = input.homeType ? HOUSE_KEEPOUT[input.homeType] : { halfX: 0, halfZ: 0 };

  const legal = (candidate: { x: number; z: number }) =>
    isWithinPlot({ position: candidate, bounds, plotTier: input.plotTier }) &&
    !overlapsHouse({ position: candidate, bounds, homeType: input.homeType });
  const crowded = (candidate: { x: number; z: number }) =>
    input.taken.some(
      (placement) => Math.hypot(placement.position.x - candidate.x, placement.position.z - candidate.z) < 1.5
    );

  let fallback: { x: number; z: number } | null = null;

  for (let push = 0; push <= 8; push += 1) {
    const candidates = faceCandidates(bounds, keepOut, Boolean(input.homeType), push);
    if (push > 0) {
      // Between the faces too, once there is room to spare.
      const ring = Math.max(keepOut.halfX + bounds.halfX, keepOut.halfZ + bounds.halfZ) + push;
      for (const angle of [Math.PI / 4, (3 * Math.PI) / 4, (5 * Math.PI) / 4, (7 * Math.PI) / 4]) {
        candidates.push({
          x: Math.cos(angle) * ring - bounds.offsetX,
          z: Math.sin(angle) * ring - bounds.offsetZ
        });
      }
    }
    for (const candidate of candidates) {
      if (!legal(candidate)) continue;
      fallback ??= candidate;
      if (!crowded(candidate)) return candidate;
    }
  }

  return fallback;
}

/**
 * The floor a home gives you to fill, in home-local voxels measured from the middle of the floor.
 *
 * Taken from the models themselves rather than invented: a tudor cottage is eight voxels across
 * and ten deep with walls five high, so the space inside its walls is what these numbers say. That
 * is the point of doing it this way — the room a member has to decorate is the room a neighbour
 * can see the outside of, and choosing a bigger home is what buys more of it.
 *
 * Heights are in authored art voxels shrunk by `ITEM_ART_SCALE`, so the tallest piece in the
 * catalogue stands 4.5 voxels. A cottage ceiling of 4 means the tall pieces genuinely do not fit
 * under it, which is a real constraint rather than a decorative one, and the hobbit hole's extra
 * head-room is a real reason to prefer it.
 */
export const HOME_INTERIOR: Record<HomeType, { halfX: number; halfZ: number; ceilingY: number }> = {
  // Dug into a hillside: the widest floor of the three and the tallest crown.
  hobbit_hole: { halfX: 5, halfZ: 4, ceilingY: 5 },
  // Up to the ceiling plane, which the cutaway lifts off along with the roof. Four is what a
  // table with something standing on it needs — the case the whole stacking rule exists for.
  tudor_cottage: { halfX: 3, halfZ: 4, ceilingY: 4 },
  cozy_cabin: { halfX: 3, halfZ: 4, ceilingY: 4 }
};

/**
 * How many things fit inside a home.
 *
 * Derived from the floor rather than fixed, because the whole progression is that a bigger home
 * holds more. A tudor's 6x8 floor takes ten pieces before it stops being a room and starts being
 * a warehouse; the hobbit hole's wider floor earns a few more.
 */
export function interiorCapacity(homeType: HomeType): number {
  const bounds = HOME_INTERIOR[homeType];
  return Math.round((bounds.halfX * 2 * bounds.halfZ * 2) / 6);
}

/**
 * True when every corner of the footprint sits inside the room.
 *
 * No edge margin, unlike the yard: furniture pushed flush against a wall is the whole look, and a
 * gap the member cannot close would read as a bug rather than as breathing room.
 */
export function isWithinHome(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  homeType: HomeType;
}): boolean {
  const limits = HOME_INTERIOR[input.homeType];
  const centreX = input.position.x + input.bounds.offsetX;
  const centreZ = input.position.z + input.bounds.offsetZ;
  return (
    Math.abs(centreX) + input.bounds.halfX <= limits.halfX &&
    Math.abs(centreZ) + input.bounds.halfZ <= limits.halfZ
  );
}

/**
 * How tall an item stands, in room voxels, at the size it is placed.
 *
 * The art's full span, not its height above its own origin — those differ for most of the
 * catalogue, and the renderer draws the span. Getting this wrong is invisible on the ground and
 * obvious the moment something is stood on something else.
 */
export function placementHeight(itemId: string, scaleStep: number): number {
  return footprintHeight(getFootprint(itemId)) * placementScale(scaleStep);
}

/** The very top of an item, ornaments and all. What the ceiling has to clear. */
export function placementTop(placement: Pick<ItemPlacement, "itemId" | "scaleStep" | "position">): number {
  return placement.position.y + placementHeight(placement.itemId, placement.scaleStep);
}

/**
 * The height of the surface an item offers — where something put on it rests.
 *
 * Lower than `placementTop` for most furniture, because the top of a table's bounding box is the
 * bowl the art stands on it, not the table. Stacking against the box is what left a potted plant
 * hovering a third of a voxel above the table it was supposed to be sitting on.
 */
export function placementSurface(placement: Pick<ItemPlacement, "itemId" | "scaleStep" | "position">): number {
  return placement.position.y +
    footprintSurface(getFootprint(placement.itemId)) * placementScale(placement.scaleStep);
}

/**
 * True when the item clears the ceiling at the size it is placed, standing at `baseY`.
 *
 * Height is measured in authored art voxels and `placementScale` shrinks it to room voxels, so the
 * tallest thing in the catalogue stands 4.5 against a ceiling of 6. On the floor nothing in the
 * catalogue reaches — it is stacking that makes this rule bite, which is the point of it.
 */
export function fitsUnderCeiling(input: {
  footprint: Footprint;
  scaleStep: number;
  homeType: HomeType;
  baseY?: number;
}): boolean {
  const top = (input.baseY ?? 0) + footprintHeight(input.footprint) * placementScale(input.scaleStep);
  return top <= HOME_INTERIOR[input.homeType].ceilingY;
}

/** Two placements sharing the same space. Touching is not overlapping — that is how stacking works. */
export function overlapsInVolume(
  a: Pick<ItemPlacement, "itemId" | "scaleStep" | "rotationQuarters" | "position">,
  b: Pick<ItemPlacement, "itemId" | "scaleStep" | "rotationQuarters" | "position">
): boolean {
  const boundsA = placementBounds(getFootprint(a.itemId), a.rotationQuarters, a.scaleStep);
  const boundsB = placementBounds(getFootprint(b.itemId), b.rotationQuarters, b.scaleStep);

  // A hair of slack, so a plant resting exactly on a table top is standing on it rather than in it.
  const slack = 1e-6;
  // Resting on counts as clear, and "on" means on the surface rather than on the bounding box —
  // otherwise a plant sitting properly on a table would read as being inside it.
  if (placementSurface(a) <= b.position.y + slack || placementSurface(b) <= a.position.y + slack) return false;

  const centreAX = a.position.x + boundsA.offsetX;
  const centreAZ = a.position.z + boundsA.offsetZ;
  const centreBX = b.position.x + boundsB.offsetX;
  const centreBZ = b.position.z + boundsB.offsetZ;
  return (
    Math.abs(centreAX - centreBX) < boundsA.halfX + boundsB.halfX - slack &&
    Math.abs(centreAZ - centreBZ) < boundsA.halfZ + boundsB.halfZ - slack
  );
}

/**
 * The surface an item would come to rest on at a given spot: the floor, or the top of whatever is
 * already standing there.
 *
 * A thing counts as supporting another when the upper item's footprint *centre* sits over it.
 * Plain rectangle overlap would snap a plant to table height for clipping one corner of it, which
 * reads as floating; asking for the centre means a thing can overhang an edge and still look
 * placed. This is the rule the drag uses, so where an item lands is where it stays.
 */
export function supportHeightAt(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  placements: ItemPlacement[];
  ignoreId?: string;
}): number {
  const centreX = input.position.x + input.bounds.offsetX;
  const centreZ = input.position.z + input.bounds.offsetZ;

  let height = 0;
  for (const other of input.placements) {
    if (other.id === input.ignoreId) continue;
    const bounds = placementBounds(getFootprint(other.itemId), other.rotationQuarters, other.scaleStep);
    const otherX = other.position.x + bounds.offsetX;
    const otherZ = other.position.z + bounds.offsetZ;
    if (Math.abs(centreX - otherX) > bounds.halfX) continue;
    if (Math.abs(centreZ - otherZ) > bounds.halfZ) continue;
    // The surface, not the summit: a plant put on a table rests on the table, not on the bowl.
    height = Math.max(height, placementSurface(other));
  }
  return height;
}

/** Whether an item has any legal position in a room at all, at a given size. */
export function fitsInHome(input: { footprint: Footprint; scaleStep: number; homeType: HomeType }): boolean {
  if (!fitsUnderCeiling(input)) return false;
  const limits = HOME_INTERIOR[input.homeType];
  for (let quarters = 0; quarters < 4; quarters += 1) {
    const bounds = placementBounds(input.footprint, quarters, input.scaleStep);
    if (bounds.halfX <= limits.halfX && bounds.halfZ <= limits.halfZ) return true;
  }
  return false;
}

/**
 * Pulls a position back inside the walls, for live dragging.
 *
 * A rectangle clamps one axis at a time, where the circular parcel needed a quadratic. Same
 * requirement though: the result has to be somewhere `isWithinHome` accepts, so the limit is shaved
 * by a hair — reconstructing the position from the clamped centre reintroduces the offset, and
 * "exactly on the wall" rounds to "just through it" often enough to matter.
 */
export function clampToInterior(input: {
  position: { x: number; z: number };
  bounds: PlacementBounds;
  homeType: HomeType;
}): { x: number; z: number } {
  if (isWithinHome(input)) return { x: input.position.x, z: input.position.z };

  const limits = HOME_INTERIOR[input.homeType];
  const axis = (centre: number, half: number, limit: number) => {
    const reach = limit - half - 1e-9;
    // Wider than the room: the middle is the least wrong place for it.
    if (reach <= 0) return 0;
    return Math.min(reach, Math.max(-reach, centre));
  };

  return {
    x: axis(input.position.x + input.bounds.offsetX, input.bounds.halfX, limits.halfX) - input.bounds.offsetX,
    z: axis(input.position.z + input.bounds.offsetZ, input.bounds.halfZ, limits.halfZ) - input.bounds.offsetZ
  };
}

/**
 * Somewhere in the room to drop a newly placed item.
 *
 * Walls first, middle last. Furniture belongs against a wall — a room that fills from the centre
 * outward looks like a warehouse — so the search offers the four walls before it offers the floor,
 * and works out from the middle of each wall so the first few pieces do not all land in a corner.
 */
export function defaultInteriorPosition(input: {
  itemId: string;
  rotationQuarters: number;
  scaleStep: number;
  homeType: HomeType;
  taken: ItemPlacement[];
}): { x: number; z: number } | null {
  const bounds = placementBounds(getFootprint(input.itemId), input.rotationQuarters, input.scaleStep);
  const limits = HOME_INTERIOR[input.homeType];
  const reachX = limits.halfX - bounds.halfX;
  const reachZ = limits.halfZ - bounds.halfZ;
  if (reachX < 0 || reachZ < 0) return null;

  // Middle of a wall first, then out toward each corner, then the open floor on a fine grid.
  // The grid matters more than it looks: the search has to agree with the overlap rule below, and
  // a handful of hand-picked spots runs out long before an eight-piece room is actually full.
  const centres: { x: number; z: number }[] = [];
  for (const slot of [0, -0.55, 0.55, -1, 1]) {
    centres.push({ x: slot * reachX, z: -reachZ });
    centres.push({ x: slot * reachX, z: reachZ });
    centres.push({ x: -reachX, z: slot * reachZ });
    centres.push({ x: reachX, z: slot * reachZ });
  }
  const step = 0.75;
  for (let z = -reachZ; z <= reachZ + 1e-9; z += step) {
    for (let x = -reachX; x <= reachX + 1e-9; x += step) {
      centres.push({ x, z });
    }
  }

  const self = {
    itemId: input.itemId,
    scaleStep: input.scaleStep,
    rotationQuarters: input.rotationQuarters
  };
  // A real overlap test rather than a distance heuristic, so that a spot this hands back is a spot
  // the validator will accept. New things arrive on the floor; stacking is something the member
  // does by dragging, not something we guess at.
  const blocked = (candidate: { x: number; z: number }) =>
    input.taken.some((placement) =>
      overlapsInVolume({ ...self, position: { x: candidate.x, y: 0, z: candidate.z } }, placement)
    );

  for (const centre of centres) {
    const candidate = { x: centre.x - bounds.offsetX, z: centre.z - bounds.offsetZ };
    if (!isWithinHome({ position: candidate, bounds, homeType: input.homeType })) continue;
    if (blocked(candidate)) continue;
    return candidate;
  }
  return null;
}

/**
 * Whether a single interior placement is legal, and if not, why in terms a member can act on.
 *
 * The same three-way split as outdoors: something that cannot fit at any size needs shrinking, and
 * something that merely landed badly needs moving. A ceiling is worth its own reason because the
 * fix is different again — nothing about the floor plan will help.
 */
export function evaluateInteriorPlacement(input: {
  itemId: string;
  itemName?: string;
  position: { x: number; y?: number; z: number };
  rotationQuarters: number;
  scaleStep: number;
  homeType: HomeType;
  /** Everything else in the same room, so a thing can stand on a table but not inside one. */
  others?: ItemPlacement[];
  /** The placement being edited, which must not be measured against itself. */
  ignoreId?: string;
}): PlacementCheck {
  const footprint = getFootprint(input.itemId);
  const bounds = placementBounds(footprint, input.rotationQuarters, input.scaleStep);
  const label = input.itemName ?? "That item";
  const baseY = input.position.y ?? 0;

  if (baseY < 0) {
    return { ok: false, reason: "outside_room", message: `${label} cannot go through the floor` };
  }
  if (!fitsInHome({ footprint, scaleStep: input.scaleStep, homeType: input.homeType })) {
    return {
      ok: false,
      reason: "too_large",
      message: input.scaleStep > 0
        ? `${label} needs more room than your home has. Make it smaller to fit it in here.`
        : `${label} needs more room than your home has.`
    };
  }
  if (!fitsUnderCeiling({ footprint, scaleStep: input.scaleStep, homeType: input.homeType, baseY })) {
    // Standing on the floor this can only be the item; stacked, it is the stack.
    return {
      ok: false,
      reason: "too_tall",
      message: baseY > 0
        ? `${label} does not fit under the ceiling stacked up there`
        : input.scaleStep > 0
          ? `${label} is taller than the ceiling. Make it smaller to fit it in here.`
          : `${label} is taller than the ceiling, even at its smallest.`
    };
  }
  if (!isWithinHome({ position: input.position, bounds, homeType: input.homeType })) {
    return { ok: false, reason: "outside_room", message: `${label} does not fit inside your home there` };
  }

  const self = {
    itemId: input.itemId,
    scaleStep: input.scaleStep,
    rotationQuarters: input.rotationQuarters,
    position: { x: input.position.x, y: baseY, z: input.position.z }
  };
  for (const other of input.others ?? []) {
    if (other.id === input.ignoreId) continue;
    if (overlapsInVolume(self, other)) {
      return { ok: false, reason: "occupied", message: `${label} would sit inside something else` };
    }
  }
  return { ok: true };
}
